import { analytics } from '@lib/analytics';
import { CATEGORY, TRACK_TYPE } from '@lib/analytics/events';
import { apiAuthCaller, apiCaller } from '@services/apiCaller';
import { SessionData } from '@stores/user';
import ErrorLogger from '@utils/errorLogger';
import { CatchErr } from '@utils/types';
import { AwsUser } from '@auth/common/util';

declare global {
  interface Window {
    analytics: unknown;
    rudderanalytics: {
      track: (a: string, b?: unknown, callback?: () => void) => void;
      page: (
        category: string,
        name: string,
        properties: unknown,
        options?: unknown,
        callback?: () => void,
      ) => void;
      identify: (a: unknown, b: unknown, c?: Record<string, unknown>) => void;
      reset: (a: true) => void;
    };
    twttr: {
      conversion: {
        trackPid: (a: string, b?: unknown) => void;
      };
    };
    rdt: (a: string, b: string) => void;
  }
}

interface AuthSignInRequest {
  name: string;
  provider: string;
  freetrial?: boolean;
  invitation?: string;
}

interface AuthSignInResponse {
  firstLogin: boolean;
  email: string;
  name: string;
}

export interface CognitoTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
}

export interface ELoginResponse extends CognitoTokens {
  email: string;
}

interface IUserAttribute {
  name?: string;
  email_verified?: string;
}

type ELoginCredentials = {
  email: string;
  password: string;
};

type SignupParams = {
  name: string;
  password: string;
  organization: string;
  token: string;
  freetrial: boolean;
  isBlendoUser: boolean;
};

type MfaType = 'NOMFA' | 'SMS';

const LOGGED_IN = 'User Logged In';
const MFA_CHALLENGE = 'User MFA Challenge';

const Auth = {
  getTokens(userSession: AwsUser['signInUserSession']) {
    const idToken = userSession.getIdToken().getJwtToken();
    const accessToken = userSession.getAccessToken().getJwtToken();
    const refreshToken = userSession.getRefreshToken().getToken();

    return { idToken, accessToken, refreshToken } as CognitoTokens;
  },

  async signup({
    name,
    password,
    organization,
    token,
    freetrial = false,
    isBlendoUser = false,
  }: SignupParams): Promise<unknown> {
    const resp = await apiCaller().post('/signup', {
      name,
      password,
      organization,
      token,
      verified: true,
      freetrial,
      isBlendoUser,
    });
    try {
      window.rudderanalytics.track('facebook_conversion_app_signup', {});
      if (window.twttr && window.twttr.conversion) {
        window.twttr.conversion.trackPid('o4ydw', { tw_sale_amount: 0, tw_order_quantity: 0 });
      }
      window.rdt('track', 'SignUp');
    } catch (error: CatchErr) {
      ErrorLogger.log(error, 'Cognito signup track error');
    }

    return resp.data;
  },

  async signupMail(email: string, freetrial: boolean): Promise<{ token: string }> {
    const resp = await apiCaller().post('/signupMail', {
      email,
      freetrial,
    });
    return resp.data;
  },

  async login(email: string, password: string): Promise<SessionData & { statusCode: number }> {
    const resp = await apiCaller().post('/login', {
      email,
      password,
    });
    const event = resp?.data?.scheme === 'smsotp' ? MFA_CHALLENGE : LOGGED_IN;
    analytics.track(event, TRACK_TYPE.API_SUCCESS, CATEGORY.AUTH, { email });
    return resp.data;
  },

  validatePassword(
    email: string,
    password: string,
  ): Promise<{ status: number; statusCode: string; message?: string }> {
    return apiCaller().post('/validatePassword', {
      email,
      password,
    });
  },

  async elogin(token: string, password: string): Promise<ELoginResponse> {
    const resp = await apiCaller().post('/login', {
      token,
      password,
    });
    window.rudderanalytics.track(LOGGED_IN, { token });
    return resp.data;
  },

  async authSignin(idToken: string, body: AuthSignInRequest) {
    const { freetrial = true } = body;
    const resp = await apiAuthCaller(idToken, true).post<AuthSignInResponse>('/authSignin', {
      ...body,
      freetrial,
    });
    return resp.data;
  },

  sendAnalytics(firstlogin: boolean, email: string) {
    if (!firstlogin) {
      window.rudderanalytics.track(LOGGED_IN, { email });
    }
  },

  async forgotPassMail(email: string): Promise<{ statusCode: number }> {
    const resp = await apiCaller().post('/forgotPassMail', {
      email,
    });
    return resp.data;
  },

  async updateAttribute(token: string, attributeList: IUserAttribute): Promise<unknown> {
    const resp = await apiAuthCaller(token, true).post('updateAttribute', {
      attributeList,
    });
    return resp.data;
  },

  async updatePassword(
    email: string,
    oldPassword: string,
    newPassword: string,
  ): Promise<{ statusCode: number }> {
    const resp = await apiCaller().post('/updatePassword', {
      email,
      oldPassword,
      newPassword,
    });
    return resp.data;
  },

  async confirmNewPassword(token: string, password: string): Promise<unknown> {
    const resp = await apiCaller().post('/confirmNewPassword', {
      token,
      password,
    });
    return resp.data;
  },

  async currentSession(
    sessionData: SessionData,
    email: string,
  ): Promise<{ status: string } & SessionData> {
    const { accessToken, idToken, refreshToken } = sessionData;
    const resp = await apiAuthCaller(idToken, true).post('/refreshToken', {
      accessToken,
      idToken,
      refreshToken,
      email,
    });
    return resp.data;
  },

  /**
   * Send SMS OTP to a phone number, while the user doesn't have SMS MFA enabled already.
   */
  async sendOtpMfaDisabled(sessionData: SessionData | undefined, phoneNumber: string) {
    if (!sessionData?.idToken) {
      return undefined;
    }

    return apiAuthCaller(sessionData.idToken, true).post('/v1/mfa/otp', { phoneNumber });
  },

  /**
   * Send SMS OTP to a **new** phone number, while the user already has MFA set up.
   * This means user will have to submit MFA for their previous phone number in order to proceed
   */
  async sendOtpMfaEnabled(sessionData: SessionData | undefined, phoneNumber: string) {
    if (!sessionData?.idToken) {
      return undefined;
    }

    return apiAuthCaller(sessionData.idToken, true).put('/v1/mfa/otp', { phoneNumber });
  },

  async registerNewMfaPhone(
    sessionData: SessionData | undefined,
    phoneNumber: string,
    code: string,
    credentials = {} as ELoginCredentials,
  ) {
    if (!sessionData?.idToken) {
      return undefined;
    }

    return apiAuthCaller(sessionData.idToken, true).post('/v1/mfa/method', {
      phoneNumber,
      code,
      ...credentials,
    });
  },

  async disableMfa(sessionData: SessionData | undefined) {
    if (!sessionData?.idToken) {
      return undefined;
    }

    return apiAuthCaller(sessionData.idToken, true).put('/v1/mfa/method', { method: 'NOMFA' } as {
      method: MfaType;
    });
  },

  async validateInvite(token: string) {
    return apiCaller().post('/acceptInvite', { token, validate: true });
  },

  async getIdpIdentifier(email: string) {
    return apiCaller().get('/v1/sso/idp', { params: { email } });
  },
};

export default Auth;
